import { memo, CSSProperties } from 'react';

import dynamic from 'next/dynamic';

import { Currencies } from '@constants/constants';
import Price from '@components/ui/price';
import { getCurrencySymbol } from '@utils/utils';
import Typography from '@components/Typography';
import CustomIcon from '@components/utils/CustomIcon';
import { WorldLogoWhite } from '@assets/icons';
import { useDevice } from '@contexts/device';

import {
  StartPriceLabel,
  StyledListingItemPriceLabel,
  ItemPriceDetails,
  PriceInEUR,
  WorldPriceWrapper,
  WorldPriceTextWrapper,
  Separator,
  WorldPriceMobileWrapper,
} from './PriceLabelStyle';

const OriginalPrice = dynamic(
  () => import('@components/ui/price/OriginalPrice')
);

export interface ListingItemLabelProps {
  style?: CSSProperties;
  originalPrice?: number | string;
  priceWithDiscount?: number | string;
  currency?: string;
  priceInEUR?: number;
  rate?: number;
  total?: boolean;
  isPackage?: boolean;
  periodType?: boolean;
  bankPosPromotionPrice?: number;
  bankPosPromotionRate?: number;
  basketHotelCount?: number;
}

const PriceLabel = ({
  originalPrice,
  priceWithDiscount,
  currency = Currencies.TL,
  priceInEUR,
  style,
  rate,
  total,
  isPackage,
  periodType,
  bankPosPromotionPrice,
  bankPosPromotionRate,
  basketHotelCount = 0,
  ...otherProps
}: ListingItemLabelProps) => {
  const { isDesktop } = useDevice();
  return (
    <StyledListingItemPriceLabel style={style} {...otherProps}>
      {priceWithDiscount ? (
        <>
          {isPackage ? (
            <Typography variant='caption' color='NL800' weight='Light'>
              Otel + Uçak + Transfer
            </Typography>
          ) : null}

          <Price
            periodType={periodType}
            total={total}
            originalPrice={
              originalPrice && !priceInEUR ? Number(originalPrice) : undefined
            }
            price={Number(priceWithDiscount)}
            originalPriceCurrency={getCurrencySymbol(currency)}
            fontSize={total || priceInEUR ? 18 : 24}
            currencyFontWeight={total || priceInEUR ? 400 : 600}
            decimalCurrencyFlexDirection='row'
            decimalFontSize={total || priceInEUR ? 18 : 24}
            showDecimal={false}
            priceInEuro={priceInEUR ? true : false}
          >
            {isPackage ? null : rate > 0 ? (
              <OriginalPrice
                price={+originalPrice}
                currency={getCurrencySymbol(currency)}
                noLine={false}
                size={16}
                decimalShow={true}
                color='NL500'
                height='18px'
              />
            ) : null}
            {priceInEUR ? (
              <PriceInEUR>
                {priceInEUR.toFixed(2) + ' ' + getCurrencySymbol(currency)}
              </PriceInEUR>
            ) : null}
          </Price>
          {basketHotelCount > 0 ? null : bankPosPromotionPrice &&
            bankPosPromotionRate ? (
            isDesktop ? (
              <WorldPriceWrapper>
                <WorldPriceTextWrapper>
                  <CustomIcon
                    icon={WorldLogoWhite}
                    width={56}
                    height={9}
                    color='NL0'
                  />
                  <Typography
                    variant='caption3'
                    weight='Bold'
                    color='NL0'
                    style={{ lineHeight: 'normal' }}
                  >
                    {`'e Özel %${bankPosPromotionRate}`}
                  </Typography>
                </WorldPriceTextWrapper>
                <Separator />
                <Price
                  periodType={false}
                  total={total}
                  price={bankPosPromotionPrice}
                  fontSize={24}
                  currencyFontWeight={600}
                  decimalFontSize={24}
                  showDecimal={false}
                  color='NL0'
                />
              </WorldPriceWrapper>
            ) : (
              <WorldPriceMobileWrapper>
                <WorldPriceTextWrapper>
                  <CustomIcon
                    icon={WorldLogoWhite}
                    width={56}
                    height={9}
                    color='NL0'
                  />
                  <Typography
                    variant='caption3'
                    weight='Bold'
                    color='NL0'
                    style={{ lineHeight: 'normal' }}
                  >
                    {`'e Özel %${bankPosPromotionRate}`}
                  </Typography>
                </WorldPriceTextWrapper>
                <Price
                  periodType={false}
                  total={total}
                  price={bankPosPromotionPrice}
                  fontSize={24}
                  currencyFontWeight={600}
                  currencyFontSize={24}
                  decimalFontSize={24}
                  showDecimal={false}
                  color='NL0'
                />
              </WorldPriceMobileWrapper>
            )
          ) : null}
          <ItemPriceDetails>
            <StartPriceLabel>
              <Typography variant='caption3' weight='SemiBold' color='NL500'>
                ‘den itibaren
              </Typography>
            </StartPriceLabel>
          </ItemPriceDetails>
        </>
      ) : null}
    </StyledListingItemPriceLabel>
  );
};

export default memo(PriceLabel);
